// Dot-globe geometry for the "Send payments easily" animation: deterministic
// land-dot field plus the sphere projection (design handoff:
// pay-across-the-world-animation.md). Lat/lon in radians unless noted.

export const GLOBE_WIDTH = 560;
export const GLOBE_HEIGHT = 230;

const RADIUS = 300;
const CENTER_X = 280;
const CENTER_Y = 360;
const TILT = 0.3;
const SAMPLES = 140_000;

export const NEW_YORK = { lat: rad(40.7), lon: rad(-74) };
export const DUBLIN = { lat: rad(53.3), lon: rad(-6.3) };

function rad(degrees: number) {
  return (degrees * Math.PI) / 180;
}

// Simplified landmass outlines [lon, lat] in degrees; dots only land on these.
const LAND: [number, number][][] = [
  [
    [-168, 66],
    [-140, 70],
    [-120, 72],
    [-95, 74],
    [-80, 70],
    [-62, 60],
    [-55, 47],
    [-70, 42],
    [-76, 35],
    [-81, 30],
    [-80, 25],
    [-90, 29],
    [-97, 26],
    [-105, 20],
    [-92, 15],
    [-83, 8],
    [-78, 7],
    [-85, 12],
    [-95, 16],
    [-105, 22],
    [-115, 30],
    [-124, 40],
    [-125, 48],
    [-140, 58],
    [-155, 58],
    [-165, 62],
  ],
  [
    [-55, 60],
    [-45, 60],
    [-20, 70],
    [-25, 80],
    [-60, 82],
    [-70, 77],
    [-60, 70],
  ],
  [
    [-80, 8],
    [-62, 10],
    [-52, 5],
    [-35, -6],
    [-39, -14],
    [-48, -26],
    [-55, -35],
    [-65, -40],
    [-66, -48],
    [-72, -54],
    [-75, -48],
    [-72, -30],
    [-80, -15],
    [-81, -4],
  ],
  [
    [-10, 36],
    [-9, 43],
    [-2, 48],
    [-5, 58],
    [5, 62],
    [12, 65],
    [20, 70],
    [30, 71],
    [45, 68],
    [70, 73],
    [100, 77],
    [130, 72],
    [150, 70],
    [170, 67],
    [180, 65],
    [180, 62],
    [160, 60],
    [155, 52],
    [140, 54],
    [135, 44],
    [130, 35],
    [122, 30],
    [118, 24],
    [108, 18],
    [104, 10],
    [100, 5],
    [103, 2],
    [98, 8],
    [92, 20],
    [87, 22],
    [80, 15],
    [78, 8],
    [77, 15],
    [72, 20],
    [68, 24],
    [60, 25],
    [57, 22],
    [52, 16],
    [44, 13],
    [43, 18],
    [35, 28],
    [35, 36],
    [28, 36],
    [24, 38],
    [19, 40],
    [15, 38],
    [12, 44],
    [8, 44],
    [3, 43],
    [-1, 38],
    [-6, 37],
  ],
  [
    [-17, 15],
    [-17, 21],
    [-10, 30],
    [-5, 36],
    [10, 37],
    [11, 33],
    [20, 32],
    [32, 31],
    [35, 28],
    [43, 12],
    [51, 12],
    [51, 3],
    [40, -5],
    [40, -15],
    [35, -22],
    [32, -29],
    [25, -34],
    [18, -34],
    [12, -18],
    [13, -5],
    [9, 4],
    [5, 5],
    [-8, 5],
    [-13, 8],
  ],
  [
    [114, -22],
    [114, -35],
    [118, -35],
    [130, -32],
    [138, -35],
    [148, -38],
    [153, -28],
    [150, -22],
    [145, -15],
    [142, -11],
    [136, -12],
    [130, -12],
    [125, -15],
    [120, -20],
  ],
  [
    [-6, 50],
    [2, 52],
    [0, 58],
    [-5, 58],
    [-8, 55],
  ],
  [
    [130, 31],
    [140, 36],
    [142, 42],
    [145, 44],
    [140, 41],
    [135, 34],
  ],
  [
    [44, -12],
    [50, -15],
    [50, -25],
    [45, -26],
    [43, -20],
  ],
  [
    [-10, 51],
    [-6, 52],
    [-6, 55],
    [-10, 55],
  ],
];

function inPolygon(x: number, y: number, poly: [number, number][]) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i]!;
    const [xj, yj] = poly[j]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

export interface GlobeDots {
  count: number;
  sinLat: Float32Array;
  cosLat: Float32Array;
  lon: Float32Array;
  radius: Float32Array;
  opacity: Float32Array;
}

let cachedDots: GlobeDots | undefined;

/** Builds the dot field once (seeded PRNG, so it is identical every time). */
export function getGlobeDots(): GlobeDots {
  if (cachedDots) return cachedDots;

  let seed = 7;
  const random = () => {
    seed = (seed * 16807) % 2147483647;
    return seed / 2147483647;
  };

  const lats: number[] = [];
  const lons: number[] = [];
  const radii: number[] = [];
  const opacities: number[] = [];
  for (let i = 0; i < SAMPLES; i++) {
    const latDeg = (Math.asin(random() * 2 - 1) * 180) / Math.PI;
    const lonDeg = random() * 360 - 180;
    // Only the front/upper slice of the sphere is ever visible.
    if (latDeg < 5 || lonDeg < -150 || lonDeg > 80) continue;
    if (!LAND.some((poly) => inPolygon(lonDeg, latDeg, poly))) continue;
    lats.push(rad(latDeg));
    lons.push(rad(lonDeg));
    radii.push(0.5 + random() * 0.7);
    opacities.push(0.3 + random() * 0.5);
  }

  cachedDots = {
    count: lats.length,
    sinLat: Float32Array.from(lats, Math.sin),
    cosLat: Float32Array.from(lats, Math.cos),
    lon: Float32Array.from(lons),
    radius: Float32Array.from(radii),
    opacity: Float32Array.from(opacities),
  };
  return cachedDots;
}

export interface ProjectedPoint {
  x: number;
  y: number;
  /** Depth from the sphere centre; negative means the far side. */
  z: number;
}

/** Projects lat/lon (optionally lifted off the surface by `lift` of R). */
export function projectLatLon(
  lat: number,
  lon: number,
  rotation: number,
  lift = 0
): ProjectedPoint {
  const rr = RADIUS * (1 + lift);
  const x = Math.cos(lat) * Math.sin(lon + rotation) * rr;
  const y = Math.sin(lat) * rr;
  const z = Math.cos(lat) * Math.cos(lon + rotation) * rr;
  const y2 = y * Math.cos(TILT) + z * Math.sin(TILT);
  const z2 = -y * Math.sin(TILT) + z * Math.cos(TILT);
  return { x: CENTER_X + x, y: CENTER_Y - y2, z: z2 };
}

/** Samples the New York to Dublin route, arcing up off the surface. */
export function getRoute(rotation: number, steps = 32): ProjectedPoint[] {
  return Array.from({ length: steps + 1 }, (_, i) => {
    const f = i / steps;
    return projectLatLon(
      NEW_YORK.lat + (DUBLIN.lat - NEW_YORK.lat) * f,
      NEW_YORK.lon + (DUBLIN.lon - NEW_YORK.lon) * f,
      rotation,
      Math.sin(f * Math.PI) * 0.2
    );
  });
}

function dotColor(x: number, y: number) {
  const u = (x + y * 0.6) / 700;
  if (u < 0.3) return "#001eff";
  if (u < 0.5) return "#8a28f3";
  if (u < 0.72) return "#e042b4";
  return "#fe634d";
}

/** Paints the visible dots onto a 560x230 canvas (already scaled for dpr). */
export function drawGlobeDots(
  ctx: CanvasRenderingContext2D,
  dots: GlobeDots,
  rotation: number
) {
  ctx.clearRect(0, 0, GLOBE_WIDTH, GLOBE_HEIGHT);
  const sinT = Math.sin(TILT);
  const cosT = Math.cos(TILT);
  for (let i = 0; i < dots.count; i++) {
    const cosLat = dots.cosLat[i]!;
    const sinLat = dots.sinLat[i]!;
    const lonRot = dots.lon[i]! + rotation;
    const x = cosLat * Math.sin(lonRot) * RADIUS;
    const y = sinLat * RADIUS;
    const z = cosLat * Math.cos(lonRot) * RADIUS;
    const z2 = -y * sinT + z * cosT;
    if (z2 < 0) continue;
    const sx = CENTER_X + x;
    const sy = CENTER_Y - (y * cosT + z * sinT);
    if (sy > 232 || sy < -4 || sx < -4 || sx > 564) continue;
    const depth = z2 / RADIUS;
    ctx.fillStyle = dotColor(sx, sy);
    ctx.globalAlpha = dots.opacity[i]! * (0.2 + depth * 0.8);
    ctx.beginPath();
    ctx.arc(sx, sy, dots.radius[i]! * (0.5 + depth * 0.7), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}
